import type { Node as PMNode } from "prosemirror-model";
import { isBlankInline, isSpacerParagraph } from "./proseMirrorNodePredicates";

export type SpacingNormalizationResult = {
  document: PMNode;
  affectedParagraphs: number;
  changed: boolean;
};

const copyWithContent = (node: PMNode, content: PMNode[]): PMNode =>
  node.type.create(node.attrs, content, node.marks);

const normalizeParagraph = (node: PMNode): { paragraphs: PMNode[]; changed: boolean } => {
  const paragraphs: PMNode[][] = [];
  let current: PMNode[] = [];
  let blankRun: PMNode[] = [];
  let hardBreaks = 0;
  let changed = false;

  const flushBlankRun = (atEnd: boolean): void => {
    if (blankRun.length === 0) return;

    if (atEnd) {
      changed = true;
    } else if (current.length === 0) {
      if (hardBreaks > 0) changed = true;
      else current.push(...blankRun);
    } else if (hardBreaks >= 2) {
      paragraphs.push(current);
      current = [];
      changed = true;
    } else {
      current.push(...blankRun);
    }

    blankRun = [];
    hardBreaks = 0;
  };

  node.forEach((child) => {
    if (isBlankInline(child)) {
      blankRun.push(child);
      if (child.type.name === "hardBreak") hardBreaks += 1;
      return;
    }

    flushBlankRun(false);
    current.push(child);
  });

  flushBlankRun(true);
  if (current.length > 0) paragraphs.push(current);

  return {
    paragraphs: paragraphs.map((content) => copyWithContent(node, content)),
    changed,
  };
};

/**
 * Normalize spacing in an already schema-valid ProseMirror document.
 *
 * The caller owns its input boundary: backend JSON callers sanitize before
 * constructing the document, while an active TipTap editor can pass its
 * current state document directly without serializing and parsing it again.
 */
export const normalizeDocumentSpacing = (document: PMNode): SpacingNormalizationResult => {
  const emptyParagraph = document.type.schema.nodes.paragraph.create();

  if (document.childCount === 1 && document.firstChild?.eq(emptyParagraph)) {
    return { document, affectedParagraphs: 0, changed: false };
  }

  const children: PMNode[] = [];
  let affectedParagraphs = 0;

  document.forEach((node) => {
    if (node.type.name !== "paragraph") {
      children.push(node);
      return;
    }

    if (isSpacerParagraph(node)) {
      affectedParagraphs += 1;
      return;
    }

    const normalized = normalizeParagraph(node);
    if (!normalized.changed) children.push(node);
    else {
      affectedParagraphs += 1;
      children.push(...normalized.paragraphs);
    }
  });

  if (affectedParagraphs === 0) {
    return { document, affectedParagraphs: 0, changed: false };
  }

  if (children.length === 0) children.push(emptyParagraph);

  return {
    document: copyWithContent(document, children),
    affectedParagraphs,
    changed: true,
  };
};
